import { memo, useMemo } from 'react'
import { pecasDeConfete, type Paleta } from '../confete'

/**
 * O confete das comemorações. Extraído do `Comemoracao.tsx` em 14/10, quando a
 * festa por marca passou a precisar de outra quantidade e outra paleta.
 *
 * **É CSS, não canvas.** São `<span>` com `animation` e nenhum
 * `requestAnimationFrame`: a animação roda na composição do navegador, fora da
 * thread do JavaScript, e por isso não disputa com o SSE nem atrasa a chegada do
 * snapshot seguinte. Num telão ligado há dias, laço de animação em JS é o tipo
 * de coisa que degrada devagar e ninguém percebe.
 *
 * `memo` com `useMemo` nas peças: a comemoração fica no ar enquanto o painel por
 * baixo continua recebendo snapshot, e sem isto as 160 peças seriam
 * reconciliadas a cada chegada — o que não as reinicia, mas gasta reconciliação
 * de graça a cada segundo do relógio.
 */

type Props = { quantidade: number; paleta?: Paleta }

function ConfetesBase({ quantidade, paleta = 'colorida' }: Props) {
  const pecas = useMemo(() => pecasDeConfete(quantidade, paleta), [quantidade, paleta])

  return (
    <div className="confetes" aria-hidden="true">
      {pecas.map(peca => (
        <span
          key={peca.id}
          className="confete"
          style={{
            left: `${peca.esquerda}%`,
            background: peca.cor,
            width: `${peca.largura}px`,
            height: `${peca.largura * 1.8}px`,
            animationDelay: `${peca.atrasoMs}ms`,
            animationDuration: `${peca.duracaoMs}ms`,
            ['--giro' as string]: `${peca.giro}deg`,
          }}
        />
      ))}
    </div>
  )
}

export const Confetes = memo(ConfetesBase)
